import { formatCurrency } from '~/common/form/Inputs';

export type RefundSummary = {
    total_refunded: string;
    donation_refunded: string;
    original_amount: string;
    count: number;
    /** Refunds the portal started that Stripe never confirmed. */
    pending_count?: number;
    pending_amount?: string;
};

const badge =
    'mt-0.5 w-fit max-w-full rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-tight tracking-wide';

/**
 * The refund flag shown under a contribution amount in the donor lists.
 *
 * Two things can be true of one donation at once: money already came back
 * (`count`), and a refund is still unconfirmed (`pending_count`). A pending
 * refund has not decremented gift_amount, so it gets its own badge instead of
 * being folded into the refunded figures — otherwise the row would understate
 * what the donor still has.
 */
const RefundBadge: React.FC<{ summary?: RefundSummary | null }> = ({
    summary,
}) => {
    if (!summary) return null;

    const pendingCount = summary.pending_count || 0;
    const pendingAmount = Number(summary.pending_amount || 0);

    return (
        <>
            {summary.count > 0 && (
                <span
                    className={`${badge} bg-gray-200 text-gray-600`}
                    title={`Original gift ${formatCurrency(Number(summary.original_amount))}, ${formatCurrency(Number(summary.donation_refunded))} refunded across ${summary.count} refund${summary.count === 1 ? '' : 's'}. The contribution shown is what the donor kept.`}
                >
                    Refunded {formatCurrency(Number(summary.donation_refunded))}{' '}
                    of {formatCurrency(Number(summary.original_amount))}
                </span>
            )}
            {pendingCount > 0 && (
                <span
                    className={`${badge} bg-warning text-black`}
                    title={`${pendingCount} refund${pendingCount === 1 ? '' : 's'} totaling ${formatCurrency(pendingAmount)} was started but never confirmed by Stripe, so the contribution above still includes it. Check the Refunds view or Stripe before re-issuing.`}
                >
                    Refund pending {formatCurrency(pendingAmount)}
                </span>
            )}
        </>
    );
};

export default RefundBadge;
